import type { Paginated, QueryParams } from '@/types'
import { buildSeed, type MockDB } from './seed'

const STORAGE_KEY = 'mcsms.db.v19'

/** Drop stale mock-DB snapshots left behind by earlier schema versions. */
function purgeOldVersions() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key && key.startsWith('mcsms.db.') && key !== STORAGE_KEY) {
        localStorage.removeItem(key)
      }
    }
  } catch {
    /* storage unavailable — nothing to clean */
  }
}

/** Hydrate the mock DB from localStorage, or seed it on first run. */
function load(): MockDB {
  purgeOldVersions()
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw) as MockDB
  } catch {
    /* ignore corrupt storage */
  }
  const seeded = buildSeed()
  persist(seeded)
  return seeded
}

let cache: MockDB | null = null

function db(): MockDB {
  if (!cache) cache = load()
  return cache
}

function persist(next: MockDB) {
  cache = next
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    /* storage full — keep in-memory only */
  }
}

/** Reset the store back to freshly seeded data. */
export function resetDb() {
  localStorage.removeItem(STORAGE_KEY)
  cache = buildSeed()
  persist(cache)
}

export function getCollection<K extends keyof MockDB>(key: K): MockDB[K] {
  return db()[key]
}

export function setCollection<K extends keyof MockDB>(key: K, value: MockDB[K]) {
  const next = { ...db(), [key]: value }
  persist(next)
}

// ---------------------------------------------------------------------------
// Network simulation
// ---------------------------------------------------------------------------

const MIN_LATENCY = 180
const MAX_LATENCY = 520
const ERROR_RATE = 0 // bump to ~0.05 to demo error states

export function latency() {
  return MIN_LATENCY + Math.random() * (MAX_LATENCY - MIN_LATENCY)
}

export async function withLatency<T>(fn: () => T): Promise<T> {
  await new Promise((r) => setTimeout(r, latency()))
  if (ERROR_RATE && Math.random() < ERROR_RATE) {
    throw new Error('Simulated network error. Please retry.')
  }
  return fn()
}

// ---------------------------------------------------------------------------
// Generic query engine — search, filter, sort, paginate
// ---------------------------------------------------------------------------

function getDeep(obj: any, path: string): unknown {
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj)
}

function matchesSearch(item: any, q: string, searchable?: string[]): boolean {
  if (!q) return true
  const needle = q.toLowerCase()
  // Tag labels are always searchable, even when a module restricts `searchable`
  // to specific fields — so typing a tag name in the search box finds tagged
  // records everywhere, matching the backend's JSONB-as-text search.
  const tagLabels = Array.isArray(item?.tags) ? item.tags.map((t: { label?: string }) => t.label).join(' ') : ''
  const haystack = searchable
    ? `${searchable.map((f) => getDeep(item, f)).join(' ')} ${tagLabels}`
    : JSON.stringify(item)
  return haystack.toLowerCase().includes(needle)
}

function matchesFilters(item: any, filters?: Record<string, unknown>): boolean {
  if (!filters) return true
  return Object.entries(filters).every(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === 'all') return true
    // "authority" filters grievances by a label inside the `authorities`
    // array field, not a top-level `authority` field.
    if (key === 'authority') {
      const list = (item.authorities ?? []) as { label?: string }[]
      const wanted = Array.isArray(value) ? value : [value]
      if (wanted.length === 0) return true
      return list.some((a) => wanted.includes(a.label))
    }
    const actual = getDeep(item, key)
    if (Array.isArray(value)) return value.length === 0 || value.includes(actual)
    return actual === value
  })
}

export function queryCollection<T>(
  items: T[],
  params: QueryParams = {},
  searchable?: string[],
): Paginated<T> {
  const { q = '', page = 1, pageSize = 25, sortBy, sortDir = 'asc', filters } = params

  let result = items.filter(
    (item) => matchesSearch(item, q, searchable) && matchesFilters(item, filters),
  )

  if (sortBy) {
    result = [...result].sort((a, b) => {
      const av = getDeep(a, sortBy) as any
      const bv = getDeep(b, sortBy) as any
      if (av == null) return 1
      if (bv == null) return -1
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? cmp : -cmp
    })
  }

  const total = result.length
  const start = (page - 1) * pageSize
  return { data: result.slice(start, start + pageSize), total, page, pageSize }
}

/** Generate the next sequential id for a collection given a prefix. */
export function nextId(prefix: string, existing: { id: string }[]): string {
  const nums = existing
    .map((e) => Number(e.id.split('-').pop()))
    .filter((n) => !Number.isNaN(n))
  const max = nums.length ? Math.max(...nums) : 0
  return `${prefix}-${String(max + 1).padStart(3, '0')}`
}
